import React, { useMemo, useState } from 'react';
import { ZoomIn, ZoomOut, Grid3x3 } from 'lucide-react';

const PIECE_COLORS = [
  { fill: '#6366f1', stroke: '#4338ca', light: '#a5b4fc', name: 'Índigo' },
  { fill: '#10b981', stroke: '#047857', light: '#6ee7b7', name: 'Esmeralda' },
  { fill: '#f59e0b', stroke: '#b45309', light: '#fcd34d', name: 'Âmbar' },
  { fill: '#ec4899', stroke: '#be185d', light: '#f9a8d4', name: 'Rosa' },
  { fill: '#14b8a6', stroke: '#0d9488', light: '#5eead4', name: 'Teal' },
  { fill: '#f97316', stroke: '#c2410c', light: '#fdba74', name: 'Laranja' },
  { fill: '#8b5cf6', stroke: '#6d28d9', light: '#c4b5fd', name: 'Violeta' },
  { fill: '#0ea5e9', stroke: '#0284c7', light: '#7dd3fc', name: 'Azul' },
];

const hashCode = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
};

const getPieceColor = (pos) => {
  const key = `${pos.part_width || pos.width || 0}-${pos.part_height || pos.height || 0}-${pos.material_name || pos.material || ''}`;
  return PIECE_COLORS[hashCode(key) % PIECE_COLORS.length];
};

const TICK_STEP = 100;

export const calculateGuillotineCutLines = (positions, boardWidth, boardHeight) => {
  if (!positions || positions.length === 0) return [];
  const lines = [];

  const addLine = (x1, y1, x2, y2, isPrimary = false) => {
    let rx1 = Math.round(x1 * 10) / 10;
    let ry1 = Math.round(y1 * 10) / 10;
    let rx2 = Math.round(x2 * 10) / 10;
    let ry2 = Math.round(y2 * 10) / 10;

    // Normalizar orientação para evitar duplicatas invertidas
    if (rx1 > rx2 || (rx1 === rx2 && ry1 > ry2)) {
      [rx1, rx2] = [rx2, rx1];
      [ry1, ry2] = [ry2, ry1];
    }

    // Ignorar linhas de comprimento zero
    if (Math.abs(rx1 - rx2) < 0.5 && Math.abs(ry1 - ry2) < 0.5) return;

    // Evitar linhas duplicadas
    const existingIdx = lines.findIndex(l => 
      Math.abs(l.x1 - rx1) < 1.5 &&
      Math.abs(l.y1 - ry1) < 1.5 &&
      Math.abs(l.x2 - rx2) < 1.5 &&
      Math.abs(l.y2 - ry2) < 1.5
    );
    if (existingIdx >= 0) {
      if (isPrimary) lines[existingIdx].isPrimary = true;
    } else {
      lines.push({ x1: rx1, y1: ry1, x2: rx2, y2: ry2, isPrimary });
    }
  };

  // 1. Identificar peças de topo e base (layout guilhotina em 3 fases padrão Corte Certo)
  const pTop = positions.find(p => {
    const y = parseFloat(p.position_y) || 0;
    const w = parseFloat(p.part_width || p.width) || 0;
    return y < 5 && w >= boardWidth * 0.5;
  });

  const pBase = positions.find(p => {
    const y = parseFloat(p.position_y) || 0;
    const w = parseFloat(p.part_width || p.width) || 0;
    return p !== pTop && y >= boardHeight * 0.45 && w >= boardWidth * 0.7;
  });

  const topCutY = pTop ? ((parseFloat(pTop.position_y) || 0) + (parseFloat(pTop.part_height || pTop.height) || 0)) : 0;
  const baseCutY = pBase ? (parseFloat(pBase.position_y) || 0) : boardHeight;
  const baseBottomY = pBase ? ((parseFloat(pBase.position_y) || 0) + (parseFloat(pBase.part_height || pBase.height) || 0)) : boardHeight;

  const coreTopY = topCutY;
  const coreBottomY = baseCutY;

  const is3Stage = (topCutY > 0 || baseCutY < boardHeight) && positions.some(p => p !== pTop && p !== pBase);

  if (is3Stage) {
    // A. Linha horizontal contínua do topo (Corte Primário Fase 1 - Vermelho)
    if (topCutY > 0 && topCutY < boardHeight) {
      addLine(0, topCutY, boardWidth, topCutY, true);
      if (pTop) {
        const topEndPx = (parseFloat(pTop.position_x) || 0) + (parseFloat(pTop.part_width || pTop.width) || 0);
        if (topEndPx < boardWidth - 1) {
          addLine(topEndPx, 0, topEndPx, topCutY, false);
        }
      }
    }

    // B. Linhas horizontais contínuas da base (Corte Primário Fase 1 - Vermelho)
    if (baseCutY > 0 && baseCutY < boardHeight) {
      addLine(0, baseCutY, boardWidth, baseCutY, true);
    }
    if (baseBottomY > 0 && baseBottomY < boardHeight - 1) {
      addLine(0, baseBottomY, boardWidth, baseBottomY, true);
    }
    if (pBase) {
      const baseEndPx = (parseFloat(pBase.position_x) || 0) + (parseFloat(pBase.part_width || pBase.width) || 0);
      if (baseEndPx < boardWidth - 1) {
        addLine(baseEndPx, baseCutY, baseEndPx, baseBottomY, false);
      }
    }

    // C. Miolo Central
    const mioloPieces = positions.filter(p => p !== pTop && p !== pBase);

    // Linhas verticais completas das colunas atravessando do topo ao fundo do miolo
    const colRightEdges = Array.from(new Set(
      mioloPieces.map(p => (parseFloat(p.position_x) || 0) + (parseFloat(p.part_width || p.width) || 0))
    )).filter(x => x < boardWidth - 1);

    colRightEdges.forEach(xEdge => {
      addLine(xEdge, coreTopY, xEdge, coreBottomY, false);
    });

    // Linhas horizontais internas de separação das peças em cada coluna
    mioloPieces.forEach(p => {
      const px = parseFloat(p.position_x) || 0;
      const py = parseFloat(p.position_y) || 0;
      const pw = parseFloat(p.part_width || p.width) || 0;
      const ph = parseFloat(p.part_height || p.height) || 0;

      // Corte superior se não for o topo do miolo
      if (py > coreTopY + 1) {
        addLine(px, py, px + pw, py, false);
      }
      // Corte inferior se não for o fundo do miolo
      if (py + ph < coreBottomY - 1) {
        addLine(px, py + ph, px + pw, py + ph, false);
      }
    });

  } else {
    // Layout padrão: arestas de corte normais das peças
    positions.forEach(pos => {
      const px = parseFloat(pos.position_x) || 0;
      const py = parseFloat(pos.position_y) || 0;
      const pw = parseFloat(pos.part_width || pos.width) || 0;
      const ph = parseFloat(pos.part_height || pos.height) || 0;

      if (py + ph < boardHeight - 1) {
        addLine(px, py + ph, px + pw, py + ph, false);
      }
      if (px + pw < boardWidth - 1) {
        addLine(px + pw, py, px + pw, py + ph, false);
      }
    });
  }

  return lines;
};

const BoardCanvas = ({ board, positions = [], onEditPosition }) => {
  const [zoom, setZoom] = useState(1);
  const [hoveredPos, setHoveredPos] = useState(null);

  const boardWidth = parseFloat(board.width) || 2750;
  const boardHeight = parseFloat(board.height) || 1850;

  const BASE_W = 600;
  const BASE_H = Math.round((boardHeight / boardWidth) * BASE_W);
  const RULER_SIZE = 20;

  const canvasW = BASE_W * zoom;
  const canvasH = BASE_H * zoom;
  const totalW = canvasW + RULER_SIZE + 4;
  const totalH = canvasH + RULER_SIZE + 4;
  const scale = canvasW / boardWidth;

  const ticks = useMemo(() => {
    const xs = [];
    const ys = [];
    for (let mm = 0; mm <= boardWidth; mm += TICK_STEP) {
      xs.push(mm);
    }
    for (let mm = 0; mm <= boardHeight; mm += TICK_STEP) {
      ys.push(mm);
    }
    return { xs, ys };
  }, [boardWidth, boardHeight]);

  const stats = useMemo(() => {
    const used = positions.reduce((sum, pos) => {
      const pw = parseFloat(pos.part_width || pos.width || 0);
      const ph = parseFloat(pos.part_height || pos.height || 0);
      return sum + pw * ph;
    }, 0);
    const total = boardWidth * boardHeight;
    const waste = total - used;
    return {
      used,
      waste,
      total,
      efficiency: total > 0 ? ((used / total) * 100).toFixed(1) : 0
    };
  }, [positions, boardWidth, boardHeight]);

  // Linhas de corte industrial guilhotina (idêntico ao Corte Certo)
  const cutLines = useMemo(() => {
    return calculateGuillotineCutLines(positions, boardWidth, boardHeight);
  }, [positions, boardWidth, boardHeight]);

  // Agrupar peças idênticas deste plano para a tabela de peças (padrão Corte Certo)
  const planPiecesSummary = useMemo(() => {
    if (!positions || positions.length === 0) return [];
    const map = new Map();

    positions.forEach((pos, idx) => {
      const sw = Math.round(parseFloat(pos.part_width || pos.width || 0));
      const sh = Math.round(parseFloat(pos.part_height || pos.height || 0));
      const code = pos.part_code || `#${idx + 1}`;
      const desc = pos.part_name || pos.description || pos.material_name || 'PEÇA';
      const key = `${code}-${sw}-${sh}-${desc}`;

      if (!map.has(key)) {
        map.set(key, {
          num: map.size + 1,
          code,
          length: Math.max(sw, sh),
          width: Math.min(sw, sh),
          quantity: 1,
          description: desc,
          color: getPieceColor(pos)
        });
      } else {
        map.get(key).quantity += 1;
      }
    });

    return Array.from(map.values());
  }, [positions]);

  // Mapa rápido de código/dimensão para o número #N da peça
  const pieceNumberMap = useMemo(() => {
    const pMap = new Map();
    planPiecesSummary.forEach(item => {
      pMap.set(`${item.code}-${item.length}x${item.width}`, item.num);
      pMap.set(`${item.code}-${item.width}x${item.length}`, item.num);
      pMap.set(item.code, item.num);
    });
    return pMap;
  }, [planPiecesSummary]);

  const handleWheel = (e) => {
    e.preventDefault();
    setZoom((z) => Math.max(0.3, Math.min(3, z - e.deltaY * 0.001)));
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 rounded-2xl shadow-xl border border-slate-200/80 p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow-lg shadow-indigo-500/30">
            <Grid3x3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-800 tracking-tight">
              Chapa #{board.board_number}
            </h3>
            <p className="text-sm text-slate-500 font-medium">
              {boardWidth.toLocaleString()} × {boardHeight.toLocaleString()} mm
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-emerald-200 shadow-sm">
            <span className="text-2xl font-black text-emerald-600">{stats.efficiency}%</span>
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-emerald-700">Utilização</span>
              <span className="text-xs text-emerald-500">{positions.length} peças</span>
            </div>
          </div>

          <div className="flex items-center bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <button
              onClick={() => setZoom((z) => Math.max(0.3, z - 0.2))}
              className="p-2.5 hover:bg-slate-100 text-slate-600 transition-colors"
              title="Afastar"
            >
              <ZoomOut className="w-5 h-5" />
            </button>
            <span className="px-4 text-sm font-bold text-slate-700 min-w-[5rem] text-center bg-slate-50">
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={() => setZoom((z) => Math.min(3, z + 0.2))}
              className="p-2.5 hover:bg-slate-100 text-slate-600 transition-colors"
              title="Aproximar"
            >
              <ZoomIn className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div
        className="relative overflow-auto rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-300 shadow-inner"
        style={{ maxHeight: '65vh' }}
        onWheel={handleWheel}
      >
        <svg
          width={totalW}
          height={totalH}
          viewBox={`0 0 ${totalW} ${totalH}`}
          className="block"
          style={{ minWidth: totalW, minHeight: totalH }}
        >
          <defs>
            <pattern id="wastePattern" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="8" stroke="#cbd5e1" strokeWidth="4" stroke-opacity="0.6" />
            </pattern>
            <pattern id="wastePatternSmall" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="6" stroke="#94a3b8" stroke-width="2" stroke-opacity="0.4" />
            </pattern>
            <filter id="pieceShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.25" />
            </filter>
            <filter id="boardGlow" x="-5%" y="-5%" width="110%" height="110%">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <linearGradient id="boardGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#f8fafc" />
              <stop offset="100%" stopColor="#f1f5f9" />
            </linearGradient>
          </defs>

          <rect x={0} y={0} width={totalW} height={RULER_SIZE} fill="#f8fafc" />
          <rect x={0} y={0} width={RULER_SIZE} height={totalH} fill="#f8fafc" />

          {ticks.xs.map((mm) => {
            const x = RULER_SIZE + (mm / boardWidth) * canvasW;
            const isMajor = mm % 500 === 0;
            return (
              <g key={`xt-${mm}`}>
                <line
                  x1={x} y1={RULER_SIZE - (isMajor ? 12 : 6)} x2={x} y2={RULER_SIZE}
                  stroke="#64748b" strokeWidth={isMajor ? 1.5 : 0.75}
                />
                {isMajor && (
                  <text x={x} y={RULER_SIZE - 14} textAnchor="middle" fontSize={10} fill="#334155" fontWeight="600">
                    {mm}
                  </text>
                )}
              </g>
            );
          })}

          {ticks.ys.map((mm) => {
            const y = RULER_SIZE + (mm / boardHeight) * canvasH;
            const isMajor = mm % 500 === 0;
            return (
              <g key={`yt-${mm}`}>
                <line
                  x1={RULER_SIZE - (isMajor ? 12 : 6)} y1={y} x2={RULER_SIZE} y2={y}
                  stroke="#64748b" strokeWidth={isMajor ? 1.5 : 0.75}
                />
                {isMajor && (
                  <text x={RULER_SIZE - 14} y={y + 3} textAnchor="end" fontSize={10} fill="#334155" fontWeight="600">
                    {mm}
                  </text>
                )}
              </g>
            );
          })}

          <rect
            x={RULER_SIZE + 6}
            y={RULER_SIZE + 6}
            width={canvasW}
            height={canvasH}
            fill="#94a3b8"
            rx={3}
            opacity={0.5}
          />

          <rect
            x={RULER_SIZE}
            y={RULER_SIZE}
            width={canvasW}
            height={canvasH}
            fill="url(#boardGradient)"
            stroke="#475569"
            strokeWidth={2}
            rx={2}
            filter="url(#boardGlow)"
          />

          <rect
            x={RULER_SIZE}
            y={RULER_SIZE}
            width={canvasW}
            height={canvasH}
            fill="url(#wastePattern)"
            rx={2}
            opacity={0.8}
          />

          <text
            x={RULER_SIZE + canvasW / 2}
            y={RULER_SIZE + canvasH + 20}
            textAnchor="middle"
            fontSize={12}
            fill="#334155"
            fontWeight="700"
          >
            {boardWidth.toLocaleString()} mm
          </text>
          <text
            x={RULER_SIZE - 16}
            y={RULER_SIZE + canvasH / 2}
            textAnchor="middle"
            fontSize={12}
            fill="#334155"
            fontWeight="700"
            transform={`rotate(-90, ${RULER_SIZE - 16}, ${RULER_SIZE + canvasH / 2})`}
          >
            {boardHeight.toLocaleString()} mm
          </text>

          {positions.map((pos, idx) => {
            const pw = parseFloat(pos.part_width || pos.width || 0);
            const ph = parseFloat(pos.part_height || pos.height || 0);
            const px = parseFloat(pos.position_x) || 0;
            const py = parseFloat(pos.position_y) || 0;
            const rot = pos.rotation || 'A';
            const sw = pw;
            const sh = ph;
            const colors = getPieceColor(pos);
            const isHovered = hoveredPos === pos.id;

            return (
              <g key={pos.id || idx}>
                <rect
                  x={RULER_SIZE + px * scale + 2}
                  y={RULER_SIZE + py * scale + 2}
                  width={Math.max(2, sw * scale)}
                  height={Math.max(2, sh * scale)}
                  fill="rgba(0,0,0,0.15)"
                  rx={4}
                />
                <rect
                  x={RULER_SIZE + px * scale}
                  y={RULER_SIZE + py * scale}
                  width={Math.max(2, sw * scale)}
                  height={Math.max(2, sh * scale)}
                  fill={colors.fill}
                  stroke={isHovered ? '#0f172a' : colors.stroke}
                  strokeWidth={isHovered ? 2.5 : 1.5}
                  rx={4}
                  filter="url(#pieceShadow)"
                  style={{ cursor: 'pointer', transition: 'all 0.15s ease' }}
                  onMouseEnter={() => setHoveredPos(pos.id)}
                  onMouseLeave={() => setHoveredPos(null)}
                  onClick={() => onEditPosition?.(pos)}
                />

                {sw * scale > 50 && sh * scale > 24 && (
                  <>
                    <text
                      x={RULER_SIZE + (px + sw / 2) * scale}
                      y={RULER_SIZE + (py + sh / 2 - 3) * scale}
                      textAnchor="middle"
                      fontSize={Math.min(13, Math.max(8, sw * scale * 0.055))}
                      fill="white"
                      fontWeight="bold"
                      style={{ textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}
                      pointerEvents="none"
                    >
                      {pos.part_code?.length > 14
                        ? pos.part_code.substring(0, 14) + '..'
                        : pos.part_code || ''}
                    </text>
                    <text
                      x={RULER_SIZE + (px + sw / 2) * scale}
                      y={RULER_SIZE + (py + sh / 2 + 11) * scale}
                      textAnchor="middle"
                      fontSize={Math.min(11, Math.max(7, sw * scale * 0.045))}
                      fill="white"
                      fillOpacity={0.95}
                      style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                      pointerEvents="none"
                    >
                      {sw}×{sh}
                    </text>
                  </>
                )}

                {/* Badge com o número #N da peça (idêntico ao Corte Certo) */}
                {sw * scale > 26 && sh * scale > 20 && (
                  <g
                    transform={`translate(${RULER_SIZE + px * scale + 4}, ${RULER_SIZE + py * scale + 4})`}
                    pointerEvents="none"
                  >
                    <rect
                      width={16}
                      height={16}
                      fill="white"
                      stroke={colors.stroke}
                      strokeWidth={1.2}
                      rx={3}
                      filter="url(#pieceShadow)"
                    />
                    <text
                      x={8}
                      y={12}
                      textAnchor="middle"
                      fontSize={10}
                      fill={colors.stroke}
                      fontWeight="bold"
                    >
                      {pieceNumberMap.get(pos.part_code) || (idx + 1)}
                    </text>
                  </g>
                )}

                {rot === 'B' && sw * scale > 45 && (
                  <g
                    transform={`translate(${RULER_SIZE + (px + sw) * scale - 18}, ${RULER_SIZE + py * scale + 4})`}
                    pointerEvents="none"
                  >
                    <rect width={15} height={15} fill="white" stroke={colors.stroke} strokeWidth={1.5} rx={3} />
                    <text x={7.5} y={11.5} textAnchor="middle" fontSize={9.5} fill={colors.stroke} fontWeight="bold">R</text>
                  </g>
                )}

                {isHovered && (
                  <>
                    <line
                      x1={RULER_SIZE + px * scale - 4}
                      y1={RULER_SIZE + py * scale - 4}
                      x2={RULER_SIZE + (px + sw) * scale + 4}
                      y2={RULER_SIZE + py * scale - 4}
                      stroke="#0f172a"
                      strokeWidth={1}
                      strokeDasharray="4,2"
                    />
                    <line
                      x1={RULER_SIZE + (px + sw) * scale + 4}
                      y1={RULER_SIZE + py * scale - 4}
                      x2={RULER_SIZE + (px + sw) * scale + 4}
                      y2={RULER_SIZE + (py + sh) * scale + 4}
                      stroke="#0f172a"
                      strokeWidth={1}
                      strokeDasharray="4,2"
                    />
                    <line
                      x1={RULER_SIZE + (px + sw) * scale + 4}
                      y1={RULER_SIZE + (py + sh) * scale + 4}
                      x2={RULER_SIZE + px * scale - 4}
                      y2={RULER_SIZE + (py + sh) * scale + 4}
                      stroke="#0f172a"
                      strokeWidth={1}
                      strokeDasharray="4,2"
                    />
                    <line
                      x1={RULER_SIZE + px * scale - 4}
                      y1={RULER_SIZE + (py + sh) * scale + 4}
                      x2={RULER_SIZE + px * scale - 4}
                      y2={RULER_SIZE + py * scale - 4}
                      stroke="#0f172a"
                      strokeWidth={1}
                      strokeDasharray="4,2"
                    />
                  </>
                )}
              </g>
            );
          })}

          {/* Linhas de corte industrial guilhotina: Vermelho (Cortes Primários) e Preto (Secundários) */}
          {cutLines.map((line, idx) => (
            <line
              key={`cut-line-${idx}`}
              x1={RULER_SIZE + line.x1 * scale}
              y1={RULER_SIZE + line.y1 * scale}
              x2={RULER_SIZE + line.x2 * scale}
              y2={RULER_SIZE + line.y2 * scale}
              stroke={line.isPrimary ? "#dc2626" : "#0f172a"}
              strokeWidth={line.isPrimary ? 2.2 : 1.6}
              strokeLinecap="square"
            />
          ))}

          {positions.length === 0 && (
            <g>
              <text
                x={RULER_SIZE + canvasW / 2}
                y={RULER_SIZE + canvasH / 2 - 10}
                textAnchor="middle"
                fontSize={16}
                fill="#64748b"
                fontWeight="600"
              >
                Nenhuma peça posicionada
              </text>
              <text
                x={RULER_SIZE + canvasW / 2}
                y={RULER_SIZE + canvasH / 2 + 14}
                textAnchor="middle"
                fontSize={12}
                fill="#94a3b8"
              >
                Arraste peças para posicionar
              </text>
            </g>
          )}
        </svg>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-4 px-1">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Cortes:</span>
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-50 border border-red-200 rounded-lg">
            <div className="w-4 h-0.5 bg-red-600 rounded-full" />
            <span className="text-xs text-red-700 font-bold">1º Cortes (Primários)</span>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-lg">
            <div className="w-4 h-0.5 bg-slate-800 rounded-full" />
            <span className="text-xs text-slate-700 font-semibold">2º Cortes (Secundários)</span>
          </div>
          <div className="w-px h-5 bg-slate-200 mx-1 hidden sm:block" />
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Legenda:</span>
          <div className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-lg">
            <div className="w-4 h-4 rounded border border-slate-300" style={{ background: 'repeating-linear-gradient(45deg, #cbd5e1, #cbd5e1 2px, #f1f5f9 2px, #f1f5f9 4px)' }} />
            <span className="text-xs text-slate-600 font-medium">Perda/Resíduo</span>
          </div>
          {PIECE_COLORS.slice(0, 5).map((c, i) => (
            <div key={i} className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-lg">
              <div className="w-4 h-4 rounded shadow-sm" style={{ backgroundColor: c.fill, border: `2px solid ${c.stroke}` }} />
              <span className="text-xs text-slate-600 font-medium">{c.name}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-5 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-500">Peças:</span>
            <span className="font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded">{positions.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-500">Usado:</span>
            <span className="font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">{(stats.used / 1000000).toFixed(2)}m²</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-500">Perda:</span>
            <span className="font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded">{(stats.waste / 1000000).toFixed(2)}m²</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-500">Chapa:</span>
            <span className="font-bold text-slate-800">{(stats.total / 1000000).toFixed(2)}m²</span>
          </div>
        </div>
      </div>

      {/* Tabela de Peças deste Plano (idêntico ao Corte Certo) */}
      {planPiecesSummary.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
              <span>📋 Peças deste plano ({positions.length} no total):</span>
            </h4>
            <span className="text-xs text-slate-500 font-medium">
              {planPiecesSummary.length} modelos de peças diferentes
            </span>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-2xs">
            <table className="min-w-full divide-y divide-slate-100 text-xs">
              <thead className="bg-slate-50">
                <tr className="text-left font-bold text-slate-600">
                  <th className="px-3 py-2 text-center w-12">#N</th>
                  <th className="px-3 py-2">Código</th>
                  <th className="px-3 py-2 text-right">Comprimento ⟷</th>
                  <th className="px-3 py-2 text-right">Largura ↕</th>
                  <th className="px-3 py-2 text-center">Qtd</th>
                  <th className="px-3 py-2">Descrição / Módulo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {planPiecesSummary.map((p) => (
                  <tr key={p.num} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-3 py-2 text-center font-bold">
                      <span
                        className="inline-flex items-center justify-center w-6 h-6 rounded-md font-bold text-xs"
                        style={{ backgroundColor: p.color.fill, color: p.color.stroke, border: `1.5px solid ${p.color.stroke}` }}
                      >
                        {p.num}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono font-semibold text-slate-700">
                      {p.code}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-slate-900">
                      {p.length} mm
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-slate-900">
                      {p.width} mm
                    </td>
                    <td className="px-3 py-2 text-center font-bold text-indigo-700">
                      <span className="px-2 py-0.5 bg-indigo-50 rounded-full border border-indigo-200">
                        {p.quantity}x
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-600 font-medium">
                      {p.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {onEditPosition && positions.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <p className="text-xs text-slate-400 text-center">
            Clique em uma peça para editar • Perda: <span className="font-semibold text-slate-600">{(100 - stats.efficiency).toFixed(1)}%</span> do material
          </p>
        </div>
      )}
    </div>
  );
};

export default BoardCanvas;
